/**
 * imageUrl.js — Source UNIQUE de construction des URLs d'images (§5/§9 DRY).
 *
 * Centralise les patterns dupliqués ~95× dans l'app :
 *   - avatar/bannière user  : `${api.baseUrl}/${path}`        (servi par l'API, /api/upload/…)
 *   - vignette collectible  : 3D → `${server3dnTech}/${dir}/${dir}.png` ; sinon IPFS `${ipfsURL}${cid}`
 *   - modèle 3D (gltf)      : `${server3dnTech}/${dir}/${gltf}`
 *
 * Toute construction d'URL image passe par ici — jamais inline dans un composant.
 */
import api from '../../core/api';

/** Avatar par défaut (fallback quand l'utilisateur n'a pas de photo). */
export const DEFAULT_AVATAR = '/img/author/author-11.png';

/**
 * Placeholder DA (data-URI SVG) affiché quand l'image d'un item échoue (404).
 * Surface claire + icône image + libellé, aux tokens DA (magenta #C218A6) : évite
 * l'icône "image cassée" native du navigateur sur les cartes/fiches (§DA middleware).
 */
export const ITEM_IMAGE_FALLBACK =
  "data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%20400%20400'%3E%3Crect%20width='400'%20height='400'%20fill='%23f4f1f6'/%3E%3Cg%20fill='none'%20stroke='%23C218A6'%20stroke-width='10'%20stroke-linecap='round'%20stroke-linejoin='round'%20opacity='0.5'%3E%3Crect%20x='120'%20y='130'%20width='160'%20height='140'%20rx='14'/%3E%3Ccircle%20cx='165'%20cy='178'%20r='16'/%3E%3Cpath%20d='M130%20252l48-44%2040%2035%2046-56%2046%2062'/%3E%3C/g%3E%3Ctext%20x='200'%20y='322'%20font-family='sans-serif'%20font-size='22'%20fill='%23999'%20text-anchor='middle'%3EImage%20unavailable%3C/text%3E%3C/svg%3E";

/**
 * URL d'un fichier servi par l'API marketplace (avatars, bannières, uploads).
 * @param {string} path - chemin relatif stocké (ex. user.picture, user.banner)
 * @returns {string|null} URL absolue, ou null si pas de chemin
 */
export const getUploadUrl = (path) => (path ? `${api.baseUrl}/${path}` : null);

/**
 * Avatar d'un utilisateur, avec fallback sur l'avatar par défaut.
 * @param {{picture?: string}} user
 * @returns {string} URL avatar (jamais vide)
 */
export const getAvatarUrl = (user) => (user && user.picture ? getUploadUrl(user.picture) : DEFAULT_AVATAR);

/**
 * Bannière d'un utilisateur (pas de fallback image — l'appelant décide).
 * @param {{banner?: string}} user
 * @returns {string|null}
 */
export const getBannerUrl = (user) => getUploadUrl(user && user.banner);

/**
 * Vignette d'un collectible/asset.
 * Asset 3D (gltf présent) → aperçu PNG 3dn ; sinon → image IPFS via cid.
 * @param {{cid?: string, directory?: string, gltf?: string}} item
 * @returns {string} URL vignette (chaîne vide si item absent)
 */
export const getItemImageUrl = (item) => {
  if (!item) return '';
  return item.gltf
    ? `${api.server3dnTech}/${item.directory}/${item.directory}.png`
    : `${api.ipfsURL}${item.cid}`;
};

/**
 * URL du modèle 3D (gltf) d'un asset, pour le viewer Nft3dCard.
 * @param {{directory?: string, gltf?: string}} item
 * @returns {string}
 */
export const getItem3dUrl = (item) => (item ? `${api.server3dnTech}/${item.directory}/${item.gltf}` : '');

/**
 * srcSet WebP multi-tailles pour un collectible 3dn (Phase B image-optim).
 * Retourne undefined pour les assets IPFS (pas de variantes WebP disponibles).
 * Convention 3dn : {dir}_400.webp (400w) + {dir}_800.webp (800w).
 * Le navigateur choisit la taille optimale ; fallback = src PNG via getItemImageUrl.
 * @param {{directory?: string, gltf?: string}} item
 * @returns {string|undefined}
 */
export const getItemSrcSet = (item) => {
  if (!item || !item.gltf) return undefined;
  const base = `${api.server3dnTech}/${item.directory}/${item.directory}`;
  return `${base}_400.webp 400w, ${base}_800.webp 800w`;
};

// SPEC-B2.3 — largeurs de variantes produites par le pipeline serveur (imageProcessing).
// Convention de nommage : <base>_<width>.webp (400/800/1600). La variante « principale » stockée
// en DB est <base>_1600.webp ; les frères _400/_800 existent à côté.
const UPLOAD_VARIANT_WIDTHS = [400, 800, 1600];
const VARIANT_RE = /_(?:400|800|1600)\.webp$/i;

/**
 * srcSet WebP responsive pour une image d'UPLOAD (avatar/bannière, colonnes user.picture/banner
 * ou nft.customImage) traitée par le pipeline serveur B2. À partir du chemin de la variante
 * principale (<base>_1600.webp), reconstruit l'ensemble _400/_800/_1600.
 * Retourne undefined si le chemin n'a pas le pattern de variante (image legacy pré-B2, non
 * ré-encodée) → le navigateur utilise simplement `src` (compat descendante).
 * @param {string|null|undefined} uploadPath  chemin relatif stocké (ex. profile/0x../uuid_1600.webp)
 * @returns {string|undefined}
 */
export const getUploadSrcSet = (uploadPath) => {
  if (!uploadPath || !VARIANT_RE.test(uploadPath)) return undefined;
  const base = uploadPath.replace(VARIANT_RE, '');
  return UPLOAD_VARIANT_WIDTHS
    .map((w) => `${getUploadUrl(`${base}_${w}.webp`)} ${w}w`)
    .join(', ');
};

/**
 * srcSet de l'avatar d'un user (variantes B2 si disponibles). Undefined pour les avatars legacy.
 * @param {{picture?: string}} user
 * @returns {string|undefined}
 */
export const getAvatarSrcSet = (user) => getUploadSrcSet(user && user.picture);

/**
 * srcSet de la bannière d'un user (variantes B2 si disponibles).
 * @param {{banner?: string}} user
 * @returns {string|undefined}
 */
export const getBannerSrcSet = (user) => getUploadSrcSet(user && user.banner);

/**
 * Titre affiché d'un exemplaire (override copy-on-write §5/§9).
 * Override de l'exemplaire (colonne NFT customTitle) sinon titre du modèle Asset.
 * @param {{customTitle?: string|null, Asset?: {title?: string}}} nft
 * @returns {string} titre affiché (chaîne vide si ni override ni modèle)
 */
export const getItemDisplayTitle = (nft) => (nft?.customTitle != null && nft?.customTitle !== '')
  ? nft.customTitle : (nft?.Asset?.title ?? '');

/**
 * Description affichée d'un exemplaire (override copy-on-write §5/§9).
 * Override de l'exemplaire (colonne NFT customDescription) sinon description du modèle Asset.
 * @param {{customDescription?: string|null, Asset?: {description?: string}}} nft
 * @returns {string} description affichée (chaîne vide si ni override ni modèle)
 */
export const getItemDisplayDescription = (nft) => (nft?.customDescription != null && nft?.customDescription !== '')
  ? nft.customDescription : (nft?.Asset?.description ?? '');

/**
 * Image « à la une » affichée d'un exemplaire (override copy-on-write §5/§9).
 * Override de l'exemplaire (fichier uploadé via l'API, colonne NFT customImage) sinon
 * image du modèle (getItemImageUrl : 3dn PNG si gltf, sinon IPFS via cid).
 * @param {{customImage?: string|null, Asset?: {cid?: string, directory?: string, gltf?: string}}} nft
 * @returns {string|null} URL image affichée
 */
export const getItemDisplayImage = (nft) => (nft?.customImage != null && nft?.customImage !== '')
  ? getUploadUrl(nft.customImage) : getItemImageUrl(nft?.Asset);
